import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { EmptyState } from '../../shared/components/EmptyState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { ErrorState } from '../../shared/components/ErrorState';
import { ModuleCard, ModuleLoadingState, ModulePageHeader } from '../../shared/components/ModulePageHeader';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import type { FeatureKeyName } from '../../../types/adminConsole';

interface FeatureKeyRow {
  id: string;
  organization_id: string;
  feature_key: string;
  enabled: boolean;
  activated_at: string | null;
}

interface AdminApiKeysPageProps {
  externalSearch?: string;
}

export const AdminApiKeysPage: React.FC<AdminApiKeysPageProps> = ({ externalSearch }) => {
  const qc = useQueryClient();
  const search = externalSearch ?? '';
  const keysQuery = useQuery({
    queryKey: ['admin-api-keys'],
    queryFn: async (): Promise<FeatureKeyRow[]> => {
      const { data, error } = await supabase
        .from('client_feature_keys')
        .select('id, organization_id, feature_key, enabled, activated_at')
        .order('organization_id', { ascending: true })
        .limit(300);

      if (error) throw error;
      return (data as FeatureKeyRow[]) || [];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ orgId, key, enabled }: { orgId: string; key: FeatureKeyName; enabled: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      return AdminConsoleService.toggleFeatureKey(orgId, key, enabled, user?.id ?? '');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-api-keys'] }),
    onError: (err) => console.error('[AdminApiKeys] toggleFeatureKey failed:', err),
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
      <div className="enterprise-page h-full overflow-y-auto">
        <ModulePageHeader icon={KeyRound} title="API Keys" subtitle="Feature keys e permissões por organização" />

        {keysQuery.isPending && <ModuleLoadingState label="Carregando chaves de API" />}

        {keysQuery.isError && (
          <ErrorState
            message="Nao foi possivel carregar as chaves configuradas."
            onRetry={keysQuery.refetch}
          />
        )}

        {!keysQuery.isPending && !keysQuery.isError && !keysQuery.data?.length && (
          <EmptyState
            title="Nenhuma chave de API registrada"
            description="Nao ha feature keys cadastradas para exibir agora."
            ctaLabel="Tentar novamente"
            onCta={() => keysQuery.refetch()}
          />
        )}

        {!keysQuery.isPending && !keysQuery.isError && Boolean(keysQuery.data?.length) && (() => {
          const filtered = (keysQuery.data ?? []).filter((row) =>
            !search ||
            row.feature_key.toLowerCase().includes(search.toLowerCase()) ||
            row.organization_id.toLowerCase().includes(search.toLowerCase())
          );
          return (
            <ModuleCard
              title={`Feature Keys (${filtered.length})`}
            >
              <div className="enterprise-table-shell">
                <table className="w-full text-left text-sm">
                  <thead className="enterprise-table-header">
                    <tr>
                      <th className="enterprise-table-head-cell">Organização</th>
                      <th className="enterprise-table-head-cell">Feature Key</th>
                      <th className="enterprise-table-head-cell">Status</th>
                      <th className="enterprise-table-head-cell">Ativação</th>
                      <th className="enterprise-table-head-cell">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr key={row.id} className="enterprise-table-row">
                        <td className="enterprise-table-cell font-mono text-[12px]">{row.organization_id}</td>
                        <td className="enterprise-table-cell font-semibold">{row.feature_key}</td>
                        <td className="enterprise-table-cell">
                          <EnterpriseStatusBadge label={row.enabled ? 'ATIVA' : 'INATIVA'} tone={row.enabled ? 'active' : 'archived'} />
                        </td>
                        <td className="enterprise-table-cell enterprise-text-secondary">
                          {row.activated_at ? new Date(row.activated_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="enterprise-table-cell">
                          <button
                            type="button"
                            onClick={() => toggleMutation.mutate({
                              orgId: row.organization_id,
                              key: row.feature_key as FeatureKeyName,
                              enabled: !row.enabled,
                            })}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${row.enabled ? 'bg-enterprise-primary' : 'bg-enterprise-border-strong'}`}
                            role="switch"
                            aria-checked={row.enabled}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${row.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ModuleCard>
          );
        })()}
      </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
